/**
 * Canonical SiteRAG URL shapes — single source for path builders and parsers.
 *
 *   /                          → portfolio dashboard
 *   /compare                   → retired; redirects to dashboard
 *   /sites/:siteId             → site landing (?tab= optional)
 *   /sites/:siteId/studio      → full-page Parcel Test-Fit Studio (generation tab)
 *   /sites/:siteId/reports/:id → report reader
 *   /sites/:siteId/reports/:id/rerun → rerun wizard (panel bridge in SiteRagReportView)
 */

export interface ParsedSiteAppPath {
  siteId: string | null;
  reportId: string | null;
  /** True when pathname ends with `/rerun` (report body + rerun panel). */
  isRerun: boolean;
  /** True when pathname is `/sites/:id/studio` (full-page test-fit studio). */
  isStudio: boolean;
}

const RERUN_PATH_RE = /^\/sites\/([^/?#]+)\/reports\/([^/?#]+)\/rerun(?:\/|$)/;
const REPORT_PATH_RE = /^\/sites\/([^/?#]+)\/reports\/([^/?#]+)(?:\/|$)/;
const STUDIO_PATH_RE = /^\/sites\/([^/?#]+)\/studio(?:\/|$)/;
const SITE_PATH_RE = /^\/sites\/([^/?#]+)/;

/** Parse `/sites/...` paths including report, rerun, and studio segments. */
export function parseSiteAppPath(pathname: string): ParsedSiteAppPath {
  const rerun = pathname.match(RERUN_PATH_RE);
  if (rerun) {
    return { siteId: rerun[1] ?? null, reportId: rerun[2] ?? null, isRerun: true, isStudio: false };
  }
  const report = pathname.match(REPORT_PATH_RE);
  if (report) {
    return { siteId: report[1] ?? null, reportId: report[2] ?? null, isRerun: false, isStudio: false };
  }
  const studio = pathname.match(STUDIO_PATH_RE);
  if (studio) {
    return { siteId: studio[1] ?? null, reportId: null, isRerun: false, isStudio: true };
  }
  const site = pathname.match(SITE_PATH_RE);
  if (site) {
    return { siteId: site[1] ?? null, reportId: null, isRerun: false, isStudio: false };
  }
  return { siteId: null, reportId: null, isRerun: false, isStudio: false };
}

/** @deprecated Prefer `parseSiteAppPath` — kept for W22-1 regression tests. */
export function extractSiteIdFromPath(pathname: string): string | null {
  return parseSiteAppPath(pathname).siteId;
}

export function isRootDashboardEntryPath(pathname: string): boolean {
  return pathname === '/' || pathname === '' || /^\/sites\/?$/.test(pathname);
}

export function isSiteScopedPath(pathname: string): boolean {
  return SITE_PATH_RE.test(pathname);
}

/** Durable URL vocabulary for the four user-facing Path-to-GO phases. */
export const PATH_TO_GO_PHASE_IDS = ['define', 'build', 'verify', 'decide'] as const;
export type SitePathToGoPhase = (typeof PATH_TO_GO_PHASE_IDS)[number];

/** Reject stale or hand-authored phase values instead of leaking them into UI state. */
export function parsePathToGoPhase(value: string | null): SitePathToGoPhase | null {
  return value != null && PATH_TO_GO_PHASE_IDS.includes(value as SitePathToGoPhase)
    ? value as SitePathToGoPhase
    : null;
}

/** Where a retired `?tab=pathtogo` link lands. */
export type LegacyPathToGoRedirectTab = 'intent' | 'reports' | 'decision';

/**
 * PERMANENT redirect for the retired Path to GO tab (Path to GO removal,
 * stage 2). Bookmarks, shared links and old notifications still carry
 * `?tab=pathtogo[&phase=…][&guided=1]`; each lands on the tab that now owns
 * that phase's work instead of silently falling back to Home:
 *
 *   phase=define                  → Intent
 *   phase=build                   → Analyses (the `reports` tab)
 *   phase=verify | decide | none  → Decision, or Analyses when the promoted
 *                                   Decision tab is rolled back (Decide then
 *                                   renders under Analyses).
 *
 * An unknown phase is treated as none. `guided=1` carries no meaning any more
 * (guided mode is retired) and the host drops it. Keep this redirect even
 * after the tab code is deleted — the old URLs outlive the tab.
 */
export function legacyPathToGoRedirectTab(
  phase: string | null,
  decisionTabVisible: boolean,
): LegacyPathToGoRedirectTab {
  const parsed = parsePathToGoPhase(phase);
  if (parsed === 'define') return 'intent';
  if (parsed === 'build') return 'reports';
  return decisionTabVisible ? 'decision' : 'reports';
}

export function siteOverviewPath(siteId: string, tab?: string): string {
  if (!tab || tab === 'overview') return `/sites/${siteId}`;
  const params = new URLSearchParams({ tab });
  return `/sites/${siteId}?${params.toString()}`;
}

/**
 * Full-page Parcel Test-Fit Studio route — the studio lifted out of the
 * generation-tab workbench into its own viewport (mirrors Simulation's
 * `/editor`). Resolves to the `generation` tab + full-screen studio overlay;
 * the workbench data pipeline is shared, so this is view state, not a
 * re-derivation. `siteOverviewPath(siteId, 'generation')` is the return door.
 */
export function studioFullPath(siteId: string): string {
  return `/sites/${siteId}/studio`;
}

export function reportSummaryPath(siteId: string, reportId: string): string {
  return `/sites/${encodeURIComponent(siteId)}/reports/${encodeURIComponent(reportId)}/summary`;
}

export function reportReaderPath(siteId: string, reportId: string): string {
  return `/sites/${siteId}/reports/${reportId}`;
}

/**
 * Open the Audit tab on one report's quality/reconciliation work queue.
 *
 * `review: 'truth'` is deliberately a separate query parameter from the
 * landing page's cross-tab `focus=` contract. The latter is owned by
 * Documents/Evidence and strips unknown focus kinds; using it here made the
 * report-specific truth-question handoff disappear during the first render.
 */
export function reportAuditPath(
  siteId: string,
  reportId: string,
  review?: 'truth',
): string {
  const params = new URLSearchParams({ tab: 'audit', report: reportId });
  if (review) params.set('review', review);
  return `/sites/${encodeURIComponent(siteId)}?${params.toString()}`;
}

export function reportRerunPath(
  siteId: string,
  reportId: string,
  query?: Record<string, string>,
): string {
  const base = `/sites/${siteId}/reports/${reportId}/rerun`;
  if (!query || Object.keys(query).length === 0) return base;
  const params = new URLSearchParams(query);
  return `${base}?${params.toString()}`;
}

/** Legacy admin bookmarks → unified identity tab. */
export function adminLegacyRedirect(pathname: string): string | null {
  if (!pathname.startsWith('/admin')) return null;
  const segment = pathname.replace(/^\/admin\/?/, '').split('/')[0];
  if (segment === 'users' || segment === 'companies' || segment === 'members') {
    return '/admin/identity';
  }
  return null;
}

/**
 * Keep the browser URL aligned with the store's active site (and report).
 * Prefer `navigate()` from call-sites; this remains a belt-and-braces sync
 * for store actions that cannot reach the router.
 */
export function syncBrowserUrlForActiveSite(siteId: string | null, reportId?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (siteId && reportId) {
      const target = `/sites/${siteId}/reports/${reportId}`;
      if (!url.pathname.startsWith(target)) {
        url.pathname = target;
        window.history.replaceState({}, '', url.toString());
      }
      return;
    }
    if (siteId) {
      const tab = url.searchParams.get('tab');
      const target = tab ? siteOverviewPath(siteId, tab) : siteOverviewPath(siteId);
      const targetPath = target.split('?')[0] ?? target;
      if (url.pathname !== targetPath && !url.pathname.startsWith(`${targetPath}/`)) {
        url.pathname = targetPath;
        if (target.includes('?')) {
          const targetUrl = new URL(target, url.origin);
          url.search = targetUrl.search;
        } else if (!tab) {
          url.search = '';
        }
        window.history.replaceState({}, '', url.toString());
      }
      return;
    }
    if (/^\/sites\/[^/?#]+/.test(url.pathname)) {
      url.pathname = '/';
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  } catch { /* SSR / sandboxed iframe */ }
}
